import React, { useRef, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { ColumnType } from "build-ts/enums";
import { useConfigurationContext } from "context/ConfigurationContext";
import KGColumnList from "modules/kgcolumn/components/KGColumnList";
import KGColumnEditForm from "modules/kgcolumn/containers/KGColumnEditForm";
import SplitView from "modules/common/SplitView";

// ---------------------------------------------------------------------------
// KGColumnDetailPanel
// ---------------------------------------------------------------------------

const KGColumnDetailPanel: React.FC<{ onReloadKnowledgeGraph: () => void; onSave: () => void }> = ({
  onReloadKnowledgeGraph,
  onSave,
}) => {
  const { selectedKgColumn, setSelectedKgColumn, setEditColumn, deleteKgColumnById } = useConfigurationContext();
  const [triggerSubmit, setTriggerSubmit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const closePanel = () => {
    setEditColumn(false);
    setSelectedKgColumn(null);
  };

  if (!selectedKgColumn) {
    return (
      <Center h="full" flexDirection="column" gap={3} color="gray.400">
        <Icon as={AppIcons.measure} boxSize={10} />
        <Text fontSize="sm" mb={0}>
          Select an item to edit its definition
        </Text>
      </Center>
    );
  }

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    await deleteKgColumnById(selectedKgColumn.id!);
  };

  return (
    <>
      <Flex direction="column" h="full">
        <Flex
          px={4}
          py={2.5}
          borderBottomWidth="1px"
          borderColor="gray.100"
          justify="space-between"
          align="center"
          flexShrink={0}
          bg="white"
          gap={3}
        >
          <Text
            fontWeight="700"
            color="gray.900"
            fontSize="sm"
            noOfLines={2}
            mb={0}
            lineHeight="1.35"
            textAlign="left"
            flex={1}
            minW={0}
          >
            {selectedKgColumn.name}
          </Text>
          <Flex gap={1} align="center" flexShrink={0}>
            <Tooltip label="Delete column" placement="top">
              <Button
                aria-label="Delete column"
                leftIcon={<Icon as={AppIcons.delete} />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                fontWeight="500"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            </Tooltip>
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<Icon as={AppIcons.checkCircle} />}
              onClick={() => setTriggerSubmit(true)}
            >
              Save
            </Button>
            <Tooltip label="Close panel" placement="top">
              <IconButton
                aria-label="Close panel"
                icon={<Icon as={AppIcons.cancelX} boxSize={5} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={closePanel}
              />
            </Tooltip>
          </Flex>
        </Flex>
        <Box flex={1} overflowY="auto">
          <KGColumnEditForm
            id={selectedKgColumn.id}
            onClose={closePanel}
            onSave={onSave}
            triggerSubmit={triggerSubmit}
            setTriggerSubmit={setTriggerSubmit}
            onReloadKnowledgeGraph={onReloadKnowledgeGraph}
          />
        </Box>
      </Flex>

      <AlertDialog
        isOpen={deleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteDialogOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Column
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete <strong>{selectedKgColumn.name}</strong>? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// KGColumnView
// ---------------------------------------------------------------------------

export interface KGColumnViewProps {
  columnType: ColumnType;
  onReloadKnowledgeGraph: () => void;
}

const KGColumnView: React.FC<KGColumnViewProps> = ({ columnType, onReloadKnowledgeGraph }) => {
  const { editColumn, selectedKgColumn, kgColumns, fetchKgColumns } = useConfigurationContext();
  const kgDetailOpen = editColumn && selectedKgColumn !== null;

  const filteredColumns = useMemo(
    () => kgColumns?.filter(c => c.column_type === columnType) ?? [],
    [kgColumns, columnType]
  );

  return (
    <SplitView
      leftPanel={
        <KGColumnList
          kgColumnList={filteredColumns}
          columnType={columnType}
          refreshKgColumns={fetchKgColumns}
          splitDetailOpen={kgDetailOpen}
        />
      }
      rightPanel={<KGColumnDetailPanel onReloadKnowledgeGraph={onReloadKnowledgeGraph} onSave={fetchKgColumns} />}
      isRightPanelVisible={kgDetailOpen}
      defaultRightWidth={35}
    />
  );
};

export default KGColumnView;
